import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BadgeModule } from 'primeng/badge';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CascadeSelectModule } from 'primeng/cascadeselect';
import { ChartModule } from 'primeng/chart';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { FieldsetModule } from 'primeng/fieldset';
import { FileUploadModule } from 'primeng/fileupload';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { PasswordModule } from 'primeng/password';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { StepsModule } from 'primeng/steps';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TooltipModule } from 'primeng/tooltip';
import { WebConsoleComponent } from './web-console.component';
import { ProfileComponent } from './profile/profile.component';
import { ApiKeyComponent } from './settings/api-key/api-key.component';
import { MembersComponent } from '@/home/team-dashboard/members/members.component';
import { LayerComponent } from './settings/layer/layer.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { TilesComponent } from './tiles/tiles.component';
import { SettingsAppsComponent } from '@/home/apps/settings-apps.component';
import { SettingsComponent } from './settings/settings.component';
import { HelpComponent } from './help/help.component';
import { ShareComponent } from '@/home/share/share.component';
import { GeneralComponent } from './settings/general/general.component';
import { StoreComponent } from '@/home/settings/store/store.component';
import { AddonsComponent } from '@/home/settings/addons/addons.component';
import { AnalyzeComponent } from './analyze/analyze.component';
import { AnalyzeOverviewComponent } from './analyze/analyze-overview/analyze-overview.component';
import { AnalyzeDetailComponent } from './analyze/analyze-detail/analyze-detail.component';
import { AddActivityComponent } from './analyze/analyze-detail/add-activity/add-activity.component';
import { DialogStartTrialComponent } from '@/home/trial/dialog-start-trial/dialog-start-trial.component';
import { DialogTrialStartedComponent } from '@/home/trial/dialog-trial-started/dialog-trial-started.component';
import { DialogTryHardwareComponent } from '@/home/trial/dialog-try-hardware/dialog-try-hardware.component';
import { DialogTrialEndedComponent } from '@/home/trial/dialog-trial-ended/dialog-trial-ended.component';
import { OnboardingComponent } from './onboarding/onboarding.component';
import { ReportCustomizationComponent } from './analyze/analyze-detail/report-customization/report-customization.component';
import { GroupComponent } from '@/home/team-dashboard/group/group.component';
import { TeamDashboardComponent } from '@/home/team-dashboard/team-dashboard.component';
import { CreateInvoiceComponent } from './analyze/analyze-detail/create-invoice/create-invoice.component';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from '@/app-routing.module';
import { HttpClientModule } from '@angular/common/http';
import { DatePicker } from 'primeng/datepicker';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { PopoverModule } from 'primeng/popover';
import { DrawerModule } from 'primeng/drawer';
import { StyleClassModule } from 'primeng/styleclass';
import { TabsModule } from 'primeng/tabs';
import { TranslateModule } from '@ngx-translate/core';
import { AppCommonModule } from '@/common/app.common.module';
import { AppLayout } from '@/theme/components/app.layout';
import { OnboardingProgressBarComponent } from '@/home/onboarding-progress-bar.component';
import { RippleModule } from 'primeng/ripple';
import { TopBarControlsComponent } from '@/home/top-bar-controls.component';
import { TrialTopBarComponent } from '@/home/trial/trial-top-bar.component';
import { TopBarNewsComponent } from '@/home/top-bar-news.component';
import { TextareaModule } from 'primeng/textarea';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IntegrationImportComponent } from '@/home/tiles/integration-import/integration-import.component';
import { TileEditDialogComponent } from '@/home/tiles/tile-edit-dialog/tile-edit-dialog.component';
import { ExternalRefsComponent } from '@/common/shared/external-refs/external-refs.component';
import { IntegrationsComponent } from '@/home/settings/integrations/integrations.component';
import { IntegrationCardComponent } from '@/home/settings/integrations/integration-card/integration-card.component';
import { IntegrationDetailsComponent } from '@/home/settings/integrations/integration-details/integration-details.component';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IntegrationUnitMappingComponent } from '@/home/settings/integrations/integration-details/integration-unit-mapping/integration-unit-mapping.component';
import { TimeBudgetBarComponent } from '@/home/tiles/time-budget-bar/time-budget-bar.component';

@NgModule({
    declarations: [
        WebConsoleComponent,
        ProfileComponent,
        ApiKeyComponent,
        MembersComponent,
        LayerComponent,
        DashboardComponent,
        TilesComponent,
        SettingsAppsComponent,
        SettingsComponent,
        HelpComponent,
        ShareComponent,
        GeneralComponent,
        StoreComponent,
        AddonsComponent,
        AnalyzeComponent,
        AnalyzeOverviewComponent,
        AnalyzeDetailComponent,
        AddActivityComponent,
        DialogStartTrialComponent,
        DialogTrialStartedComponent,
        DialogTryHardwareComponent,
        DialogTrialEndedComponent,
        OnboardingComponent,
        ReportCustomizationComponent,
        GroupComponent,
        TeamDashboardComponent,
        CreateInvoiceComponent,
        IntegrationImportComponent,
        OnboardingProgressBarComponent,
        TopBarControlsComponent,
        TrialTopBarComponent,
        TopBarNewsComponent,
        TileEditDialogComponent,
        IntegrationsComponent,
        TimeBudgetBarComponent,
    ],
    imports: [
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        AppRoutingModule,
        HttpClientModule,
        AutoCompleteModule,
        BadgeModule,
        BreadcrumbModule,
        ButtonModule,
        DatePicker,
        CardModule,
        CascadeSelectModule,
        ChartModule,
        CheckboxModule,
        ChipModule,
        ConfirmDialogModule,
        ColorPickerModule,
        DialogModule,
        DividerModule,
        SelectModule,
        FieldsetModule,
        FileUploadModule,
        InputNumberModule,
        InputGroupModule,
        InputGroupAddonModule,
        ToggleSwitchModule,
        InputTextModule,
        ListboxModule,
        MenuModule,
        MessageModule,
        MultiSelectModule,
        PopoverModule,
        PasswordModule,
        ProgressBarModule,
        ProgressSpinnerModule,
        RadioButtonModule,
        RippleModule,
        SelectButtonModule,
        DrawerModule,
        SkeletonModule,
        StepsModule,
        StyleClassModule, // TODO: do we really need this? rarely used...
        TableModule,
        TabsModule,
        TagModule,
        ToastModule,
        ToggleButtonModule,
        TooltipModule,
        TranslateModule,
        AppCommonModule,
        TextareaModule,
        IconFieldModule,
        InputIconModule,
        FloatLabelModule,
        // Theme:
        AppLayout,
        ExternalRefsComponent,
        IntegrationCardComponent,
        IntegrationDetailsComponent,
        IntegrationUnitMappingComponent,
    ],
    exports: [WebConsoleComponent],
})
export class WebConsoleModule {}
